<template>
  <div class="visor-panel">
    <!-- ── Body ──────────────────────────────────────────────────── -->
    <div class="panel-body">
      <!-- Estado: cargando -->
      <div v-if="cargando" class="state-box">
        <span class="material-symbols-outlined spin" style="font-size:28px">progress_activity</span>
        <span>Cargando datos de la planilla...</span>
      </div>
      <!-- Estado: error -->
      <div v-else-if="errorMsg" class="state-box state-error">
        <span class="material-symbols-outlined" style="font-size:28px">error</span>
        <span>{{ errorMsg }}</span>
        <button class="btn-action info" style="max-width:160px;margin-top:6px" @click="cargarDatos">
          <span class="material-symbols-outlined">refresh</span> Reintentar
        </button>
      </div>
      <!-- Contenido principal -->
      <div v-else class="layout">
        <!-- ── COLUMNA IZQUIERDA ──────────────────────────────────── -->
        <div class="col-left">
          <div class="selector-box">
            <div class="selector-head">
              <span class="material-symbols-outlined" style="font-size:15px">list</span>
              Elementos del detalle
            </div>
            <div class="search-wrap">
              <input
                v-model="busqueda"
                class="search-input"
                placeholder="Buscar por código o descripción..."
              />
            </div>
            <div class="det-list">
              <div
                v-for="grupo in gruposFiltrados"
                :key="grupo.codigo"
                class="det-item"
                :class="{ selected: codigoSeleccionado === grupo.codigo }"
                @click="toggleSeleccion(grupo.codigo)"
              >
                <div style="flex:1;min-width:0">
                  <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px">
                    <span class="det-code">{{ grupo.codigo }}</span>
                    <span class="det-chip green">×{{ grupo.totalCant }}</span>
                    <span class="det-chip">${{ grupo.totalVal.toFixed(2) }}</span>
                  </div>
                  <div class="det-desc">{{ grupo.descripcion }}</div>
                  <div class="det-info">
                    <span style="font-size:.67rem;color:#9ba3b2">
                      {{ grupo.fechas.length }} fecha(s)
                    </span>
                  </div>
                </div>
                <span
                  v-if="codigoSeleccionado === grupo.codigo"
                  class="material-symbols-outlined"
                  style="color:#7c3aed;font-size:16px;flex-shrink:0"
                >check_circle</span>
              </div>
              <div v-if="!gruposFiltrados.length" class="no-sel" style="padding:16px">
                Sin resultados
              </div>
            </div>
          </div>

          <div class="view-toggle">
            <button
              type="button" class="vtbtn"
              :class="{ active: vista === 'cal' }"
              @click="vista = 'cal'"
            >
              <span class="material-symbols-outlined" style="font-size:14px">calendar_month</span>
              Calendario
            </button>
            <button
              type="button" class="vtbtn"
              :class="{ active: vista === 'tbl' }"
              @click="vista = 'tbl'"
            >
              <span class="material-symbols-outlined" style="font-size:14px">table</span>
              Tabla
            </button>
          </div>

          <div v-if="vista === 'tbl'" class="selector-box tbl-wrap">
            <table class="det-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th>Fecha</th>
                  <th>Cant.</th>
                  <th>Valor uni.</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="det in detalles"
                  :key="det.idPlanillaDet"
                  :class="{ 'row-highlight': codigoSeleccionado === det.elementoCodigo }"
                  style="cursor:pointer"
                  @click="toggleSeleccion(det.elementoCodigo)"
                >
                  <td class="td-code">{{ det.elementoCodigo }}</td>
                  <td class="td-desc" :title="det.elementoNombre">{{ det.elementoNombre }}</td>
                  <td class="td-fecha">{{ toDateStr(det.fechaAtencion) }}</td>
                  <td class="td-cant">{{ det.cantidad }}</td>
                  <td class="td-val">${{ Number(det.valorUni).toFixed(2) }}</td>
                  <td class="td-val">${{ Number(det.valorTotal).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div><!-- /col-left -->

        <!-- ── COLUMNA DERECHA: calendario ───────────────────────── -->
        <div class="col-right">
          <div class="cal-box">
            <div class="cal-head">
              <span class="material-symbols-outlined" style="font-size:15px">calendar_month</span>
              Grilla de fechas
              <span v-if="planilla" style="margin-left:auto;font-size:.72rem;color:#9ba3b2;font-weight:400">
                {{ toDateStr(planilla.fechaDesde) }} → {{ toDateStr(planilla.fechaHasta) }}
              </span>
            </div>

            <div v-if="codigoSeleccionado && grupoSeleccionado" class="sel-detail">
              <div class="sel-detail-title">{{ codigoSeleccionado }}</div>
              <div class="sel-detail-sub">
                {{ grupoSeleccionado.descripcion.substring(0, 90) }}
                {{ grupoSeleccionado.descripcion.length > 90 ? '…' : '' }}
              </div>
              <div class="sel-stats">
                <div class="sel-stat"><span>Registros</span><strong>{{ grupoSeleccionado.registros }}</strong></div>
                <div class="sel-stat"><span>Total cant.</span><strong>{{ grupoSeleccionado.totalCant }}</strong></div>
                <div class="sel-stat"><span>Total $</span><strong>${{ grupoSeleccionado.totalVal.toFixed(2) }}</strong></div>
                <div class="sel-stat"><span>Fechas</span><strong>{{ grupoSeleccionado.fechas.length }}</strong></div>
              </div>
            </div>

            <div class="cal-legend">
              <div class="legend-item"><div class="legend-dot ld-range"></div>Rango planilla</div>
              <div class="legend-item"><div class="legend-dot ld-sel"></div>Elemento seleccionado</div>
            </div>

            <div class="months-container">
              <template v-if="planilla">
                <div v-for="mes in mesesRango" :key="mes.key">
                  <div class="cal-month">{{ mes.label }}</div>
                  <div class="cal-grid">
                    <div v-for="d in ['Do','Lu','Ma','Mi','Ju','Vi','Sá']" :key="d" class="cal-dow">{{ d }}</div>
                    <div v-for="n in mes.primerDia" :key="'e'+n"></div>
                    <div
                      v-for="day in mes.diasEnMes"
                      :key="day"
                      class="cal-day"
                      :class="clasesDia(mes.year, mes.month, day)"
                      :title="tituloDia(mes.year, mes.month, day)"
                    >
                      {{ day }}
                      <span
                        v-if="cantidadEnFecha(mes.year, mes.month, day) !== null"
                        class="cal-qty"
                      >{{ cantidadEnFecha(mes.year, mes.month, day) }}</span>
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="no-sel">
                <span class="material-symbols-outlined" style="font-size:28px">event_busy</span>
                <p>No hay datos de planilla disponibles</p>
              </div>
              <div v-if="planilla && !codigoSeleccionado" class="no-sel" style="border:none;padding-top:0">
                <span class="material-symbols-outlined" style="font-size:24px">touch_app</span>
                <p>Selecciona un elemento de la lista</p>
              </div>
            </div>
          </div>
        </div><!-- /col-right -->
      </div><!-- /layout -->
    </div><!-- /panel-body -->
  </div>
</template>

<script>
import api from "@/services/api";

const MESES_ES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

export default {
  name: "VisorDetallePlanilla",

  props: {
    idPlanilla: { type: [Number, String], required: true },
  },

  emits: [],

  data() {
    return {
      cargando:           false,
      errorMsg:           "",
      planilla:           null,
      detalles:           [],
      busqueda:           "",
      codigoSeleccionado: null,
      vista:              "cal",
    };
  },

  computed: {
    grupos() {
      const map = {};
      this.detalles.forEach(d => {
        if (!map[d.elementoCodigo]) {
          map[d.elementoCodigo] = {
            codigo:      d.elementoCodigo,
            descripcion: d.elementoNombre || "",
            totalCant:   0,
            totalVal:    0,
            registros:   0,
            fechas:      [],
            fechasCant:  {},
          };
        }
        const fecha = this.toDateStr(d.fechaAtencion);
        map[d.elementoCodigo].totalCant += Number(d.cantidad) || 0;
        map[d.elementoCodigo].totalVal  += Number(d.valorTotal) || 0;
        map[d.elementoCodigo].registros += 1;
        map[d.elementoCodigo].fechasCant[fecha] =
          (map[d.elementoCodigo].fechasCant[fecha] || 0) + (Number(d.cantidad) || 0);
        if (!map[d.elementoCodigo].fechas.includes(fecha)) {
          map[d.elementoCodigo].fechas.push(fecha);
        }
      });
      return Object.values(map);
    },

    gruposFiltrados() {
      const q = this.busqueda.toLowerCase();
      if (!q) return this.grupos;
      return this.grupos.filter(
        g =>
          g.codigo.toLowerCase().includes(q) ||
          g.descripcion.toLowerCase().includes(q)
      );
    },

    codigosUnicos() {
      return this.grupos.map(g => g.codigo);
    },

    grupoSeleccionado() {
      if (!this.codigoSeleccionado) return null;
      return this.grupos.find(g => g.codigo === this.codigoSeleccionado) || null;
    },

    mesesRango() {
      if (!this.planilla) return [];
      const desde = this.toDateStr(this.planilla.fechaDesde);
      const hasta = this.toDateStr(this.planilla.fechaHasta);
      if (!desde || !hasta) return [];
      const meses = [];
      const [y0, m0] = desde.split("-").map(Number);
      const [y1, m1] = hasta.split("-").map(Number);
      let y = y0, m = m0;
      while (y < y1 || (y === y1 && m <= m1)) {
        meses.push({
          key:       `${y}-${m}`,
          label:     `${MESES_ES[m - 1]} ${y}`,
          year:      y,
          month:     m,
          primerDia: new Date(y, m - 1, 1).getDay(),
          diasEnMes: new Date(y, m, 0).getDate(),
        });
        m++;
        if (m > 12) { m = 1; y++; }
      }
      return meses;
    },
  },

  watch: {
    idPlanilla() {
      this.reset();
      this.cargarDatos();
    },
  },

  mounted() {
    this.cargarDatos();
  },

  methods: {
    toDateStr(iso) {
      if (!iso) return "";
      return String(iso).substring(0, 10);
    },

    padDate(y, m, d) {
      return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    },

    async cargarDatos() {
      this.cargando = true;
      this.errorMsg = "";
      try {
        const [resPlanilla, resDets] = await Promise.all([
          api.get(`/vwplanillas/${this.idPlanilla}`),
          api.get(`/vwplanilladets/idplanilla/${this.idPlanilla}`),
        ]);
        this.planilla = resPlanilla.data;
        this.detalles = Array.isArray(resDets.data) ? resDets.data : [];
      } catch (err) {
        this.errorMsg =
          err.response?.data?.message ||
          err.message ||
          "Error al cargar los datos";
      } finally {
        this.cargando = false;
      }
    },

    reset() {
      this.planilla           = null;
      this.detalles           = [];
      this.codigoSeleccionado = null;
      this.busqueda           = "";
      this.errorMsg           = "";
    },

    toggleSeleccion(codigo) {
      this.codigoSeleccionado =
        this.codigoSeleccionado === codigo ? null : codigo;
    },

    clasesDia(year, month, day) {
      const dateStr = this.padDate(year, month, day);
      const desde   = this.toDateStr(this.planilla?.fechaDesde);
      const hasta   = this.toDateStr(this.planilla?.fechaHasta);
      const today   = new Date().toISOString().substring(0, 10);
      return {
        "in-range": desde && hasta && dateStr >= desde && dateStr <= hasta,
        "has-item": this.grupoSeleccionado &&
                    this.grupoSeleccionado.fechasCant[dateStr] !== undefined,
        "today":    dateStr === today,
      };
    },

    cantidadEnFecha(year, month, day) {
      if (!this.grupoSeleccionado) return null;
      const cant = this.grupoSeleccionado.fechasCant[this.padDate(year, month, day)];
      return cant !== undefined ? cant : null;
    },

    tituloDia(year, month, day) {
      const dateStr = this.padDate(year, month, day);
      const cant    = this.cantidadEnFecha(year, month, day);
      return cant !== null ? `${dateStr} — cantidad: ${cant}` : dateStr;
    },
  },
};
</script>

<style scoped>
.visor-panel {
  --accent:         #2563eb;
  --accent-light:   #dbeafe;
  --accent-muted:   #eff6ff;
  --green:          #16a34a;
  --green-light:    #dcfce7;
  --selected:       #7c3aed;
  --selected-light: #ede9fe;
  --selected-bg:    #f5f3ff;
  --amber:          #d97706;
  --border:         #e2e6ea;
  --border2:        #d0d7de;
  --surface:        #ffffff;
  --surface2:       #f8f9fb;
  --text:           #1a1d23;
  --text2:          #5a6072;
  --text3:          #9ba3b2;

  background: var(--surface2);
}

.panel-body { padding: 14px 16px; background: var(--surface2); }

.state-box {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 36px; color: var(--text3); font-size: .85rem; gap: 8px; text-align: center;
}
.state-error { color: #dc2626; }
@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin 1s linear infinite; }

.layout { display: grid; grid-template-columns: 1fr 290px; gap: 14px; align-items: start; }
.col-left { display: flex; flex-direction: column; gap: 10px; }

.selector-box { background: var(--surface); border: 1px solid var(--border); border-radius: 8px; overflow: hidden; }
.selector-head {
  padding: 8px 12px; background: var(--surface2); border-bottom: 1px solid var(--border);
  font-weight: 700; font-size: .78rem; color: var(--text2); display: flex; align-items: center; gap: 6px;
}
.search-wrap { padding: 8px 10px; border-bottom: 1px solid var(--border); }
.search-input {
  width: 100%; padding: 5px 10px 5px 30px;
  border: 1px solid var(--border2); border-radius: 6px;
  font-size: .8rem; color: var(--text); background: var(--surface);
  outline: none; transition: border .15s;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%239ba3b2' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.35-4.35'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: 8px center;
}
.search-input:focus { border-color: var(--accent); }

.det-list { max-height: 300px; overflow-y: auto; }
.det-item {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 7px 10px; cursor: pointer;
  border-bottom: 1px solid var(--border); transition: background .12s;
}
.det-item:last-child { border-bottom: none; }
.det-item:hover      { background: var(--accent-muted); }
.det-item.selected   { background: var(--selected-bg); border-left: 3px solid var(--selected); }
.det-item.selected .det-code { color: var(--selected); }
.det-code { font-weight: 700; color: var(--accent); font-size: .75rem; white-space: nowrap; min-width: 52px; }
.det-desc {
  font-size: .72rem; color: var(--text2); line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.det-info { display: flex; gap: 6px; margin-top: 2px; }
.det-chip { font-size: .68rem; padding: 1px 6px; border-radius: 10px; background: var(--accent-light); color: var(--accent); font-weight: 600; }
.det-chip.green { background: var(--green-light); color: var(--green); }

.view-toggle { display: flex; }
.vtbtn {
  flex: 1; padding: 5px 8px; font-size: .75rem; font-weight: 600;
  border: 1px solid var(--border2); background: var(--surface);
  cursor: pointer; color: var(--text2); transition: all .12s;
  display: flex; align-items: center; justify-content: center; gap: 4px;
}
.vtbtn:first-child { border-radius: 6px 0 0 6px; }
.vtbtn:last-child  { border-radius: 0 6px 6px 0; border-left: none; }
.vtbtn.active      { background: var(--accent); color: #fff; border-color: var(--accent); }

.tbl-wrap { overflow-x: auto; }
.det-table { width: 100%; border-collapse: collapse; font-size: .76rem; }
.det-table th {
  background: var(--surface2); padding: 5px 8px; text-align: left;
  font-weight: 700; color: var(--text2); border-bottom: 2px solid var(--border2);
  white-space: nowrap; font-size: .7rem; text-transform: uppercase; letter-spacing: .04em;
}
.det-table td { padding: 5px 8px; border-bottom: 1px solid var(--border); vertical-align: middle; }
.det-table tr:hover td { background: var(--accent-muted); }
.row-highlight td      { background: var(--selected-bg) !important; }
.td-code  { font-weight: 700; color: var(--accent); }
.td-desc  { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text2); }
.td-cant  { text-align: center; font-weight: 700; }
.td-val   { text-align: right; color: var(--green); font-weight: 600; font-size: .72rem; }
.td-fecha { color: var(--text2); white-space: nowrap; font-size: .72rem; }

.cal-box { background: var(--surface); border: 1px solid var(--border); border-radius: 8px; overflow: hidden; }
.cal-head {
  padding: 8px 12px; background: var(--surface2); border-bottom: 1px solid var(--border);
  font-weight: 700; font-size: .78rem; color: var(--text2); display: flex; align-items: center; gap: 6px;
}

.sel-detail {
  margin: 8px 10px; padding: 8px 10px;
  background: var(--selected-bg); border: 1px solid var(--selected-light);
  border-radius: 7px; font-size: .75rem; color: var(--selected);
}
.sel-detail-title { font-weight: 700; margin-bottom: 2px; }
.sel-detail-sub   { color: var(--text2); font-size: .7rem; line-height: 1.3; }
.sel-stats { display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap; }
.sel-stat {
  display: flex; flex-direction: column; align-items: center;
  background: #fff; border-radius: 6px; padding: 4px 8px;
  border: 1px solid var(--selected-light); font-size: .68rem; color: var(--text2);
}
.sel-stat strong { font-size: .8rem; color: var(--selected); }

.cal-legend { display: flex; gap: 10px; padding: 6px 10px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
.legend-item { display: flex; align-items: center; gap: 4px; font-size: .7rem; color: var(--text2); }
.legend-dot  { width: 10px; height: 10px; border-radius: 3px; }
.ld-range    { background: var(--accent-light); border: 1px solid var(--accent); }
.ld-sel      { background: var(--selected-light); border: 1px solid var(--selected); }

.months-container { padding: 8px; max-height: 400px; overflow-y: auto; }
.cal-month {
  text-align: center; font-size: .72rem; font-weight: 700;
  color: var(--text2); padding: 4px 0 2px; text-transform: capitalize;
}
.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; padding: 4px 0 8px; }
.cal-dow {
  text-align: center; font-size: .62rem; font-weight: 700;
  color: var(--text3); padding: 2px 0; text-transform: uppercase; letter-spacing: .05em;
}
.cal-day {
  aspect-ratio: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  border-radius: 6px; font-size: .72rem; font-weight: 600;
  color: var(--text3); background: transparent; gap: 1px; transition: all .15s;
}
.cal-day.in-range { background: var(--accent-light); color: var(--accent); font-weight: 700; }
.cal-day.has-item {
  background: var(--selected-light); color: var(--selected);
  font-weight: 800; border: 2px solid var(--selected);
  box-shadow: 0 0 0 2px rgba(124,58,237,.15);
}
.cal-day.in-range.has-item {
  background: linear-gradient(135deg, var(--selected-light) 60%, var(--accent-light));
}
.cal-day.today { outline: 2px solid var(--amber); outline-offset: 1px; }
.cal-qty {
  font-size: .56rem; font-weight: 800; color: var(--selected);
  background: #fff; border-radius: 5px; padding: 0 3px;
  line-height: 1.4; min-width: 14px; text-align: center;
}

.no-sel {
  display: flex; flex-direction: column; align-items: center;
  padding: 20px 12px; color: var(--text3); font-size: .8rem;
  gap: 6px; text-align: center;
  border: 2px dashed var(--border); border-radius: 6px;
  background: var(--surface); margin: 8px 0;
}

.btn-action {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 7px 14px; border: none; border-radius: 6px;
  font-size: .85rem; font-weight: 600; cursor: pointer; transition: all .15s; width: 100%;
}
.btn-action.info       { background: #0dcaf0; color: #000; }
.btn-action.info:hover { background: #0bb5d6; }
</style>